import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// True when the argument looks like an axios/network Error rather than a
// bare `detail` payload. Guards the helpers below from ever stringifying an
// Error object (which surfaces "AxiosError: Request failed with status..." to
// the user).
function _looksLikeAxiosError(x) {
  if (x == null) return false;
  if (typeof x !== "object") return false;
  return !!(x.isAxiosError || x instanceof Error || x.response !== undefined || x.request !== undefined || x.config !== undefined);
}

/**
 * Normalize a FastAPI `detail` payload (or a raw axios Error) into a concise
 * plain-English message. Accepts either:
 *   - a string / list of {msg} / {msg: string} — used as-is
 *   - an axios/network Error — auto-unwraps `err.response.data.detail`; if
 *     the server sent no useful detail, returns a generic fallback rather
 *     than leaking an "AxiosError: Request failed..." string to the user.
 *   - null / undefined — returns the generic fallback.
 * Never leaks HTTP status codes or Error prototypes to the caller.
 */
export function formatApiErrorDetail(input) {
  if (_looksLikeAxiosError(input)) {
    return formatApiError(input);
  }
  if (input == null) return "Something went wrong. Please try again.";
  if (typeof input === "string") return input.trim() || "Something went wrong. Please try again.";
  if (Array.isArray(input)) {
    const parts = input
      .map((e) => (e && typeof e.msg === "string" ? e.msg : typeof e === "string" ? e : null))
      .filter(Boolean);
    return parts.length ? parts.join(" ") : "Something went wrong. Please try again.";
  }
  if (typeof input === "object" && typeof input.msg === "string") return input.msg;
  // Defensive: never String() an unknown object into the UI — it can leak
  // developer-facing junk. Fall back to the plain generic message.
  return "Something went wrong. Please try again.";
}

/**
 * Canonical Admin error formatter. Given a raw axios/network Error, return
 * a plain-English message using the backend `detail` when it exists,
 * otherwise a concise action-specific fallback. Also console.errors the
 * full context so a DevTools screenshot preserves diagnostic info without
 * that developer garbage bleeding into the user-visible toast.
 */
export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  // Diagnostic breadcrumb — screenshots of DevTools still show what happened.
  if (_looksLikeAxiosError(err)) {
    try {
      // eslint-disable-next-line no-console
      console.error("[bbk.api]", {
        message: err?.message,
        code: err?.code,
        response_status: status,
        response_data: err?.response?.data,
        request_url: err?.config?.url,
        request_method: err?.config?.method,
      });
    } catch (_) {}
  }

  // Case A — backend gave us a real detail (string / list / {msg}). Use it.
  if (detail != null) {
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map((d) => (d && typeof d.msg === "string" ? d.msg : typeof d === "string" ? d : null))
        .filter(Boolean);
      if (parts.length) return parts.join(" ");
    }
    if (typeof detail === "object" && typeof detail.msg === "string" && detail.msg.trim()) return detail.msg;
    // detail existed but was unusable — deliberately fall through so we
    // don't ever leak Error-object stringification.
  }

  // Case B — server responded but no detail (HTML 5xx from ingress, etc.).
  if (status && status >= 500) {
    return "The server hit a problem. Please try again in a moment. If it keeps happening, contact support@bulkbuykits.com.";
  }
  if (status === 429) {
    return "Too many requests. Please wait a moment and try again.";
  }

  // Case C — no response at all (network / CORS / TLS / ingress timeout).
  if (_looksLikeAxiosError(err) && err?.request && !err?.response) {
    return "The server didn't respond. Check your connection and try again.";
  }

  return fallback;
}
